import {
  fromCents,
  italianToday,
  parseDecimal,
  type PlannedTransferRequest,
  type ScheduleAccount,
} from '@k-management/shared';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';

/** A planned transfer between two of our accounts, new (maybe suggested) or to change. */
export function PlannedTransferDialog({
  title,
  accounts,
  initial,
  busy,
  error,
  onSave,
  onClose,
}: {
  title: string;
  accounts: ScheduleAccount[];
  initial: Partial<PlannedTransferRequest>;
  busy: boolean;
  error?: string | null;
  onSave: (body: PlannedTransferRequest) => void;
  onClose: () => void;
}) {
  const [fromCode, setFromCode] = useState(initial.fromCode ?? accounts[0]?.code ?? '');
  const [toCode, setToCode] = useState(
    initial.toCode ?? accounts.find((item) => item.code !== fromCode)?.code ?? '',
  );
  const [amount, setAmount] = useState(initial.amount ?? '');
  const [date, setDate] = useState(initial.date ?? italianToday());
  const [note, setNote] = useState(initial.note ?? '');
  const valid =
    fromCode !== '' &&
    toCode !== '' &&
    fromCode !== toCode &&
    (parseDecimal(amount) ?? 0) > 0 &&
    date !== '';
  const options = accounts.map((item) => (
    <option key={item.code} value={item.code}>
      {item.alias}
    </option>
  ));
  return (
    <ConfirmDialog
      title={title}
      confirmLabel="Salva"
      busy={busy}
      disabled={!valid}
      error={error}
      onConfirm={() =>
        onSave({
          fromCode,
          toCode,
          amount: fromCents(Math.round((parseDecimal(amount) ?? 0) * 100)),
          date,
          note: note.trim() === '' ? null : note,
        })
      }
      onCancel={onClose}
    >
      <p className="muted small">
        Il giroconto programmato sposta il saldo previsto dei due conti alla data indicata. Si
        chiude quando lo colleghi al movimento di banca.
      </p>
      <div className="row">
        <Field label="Dal conto">
          <select value={fromCode} onChange={(event) => setFromCode(event.target.value)}>
            {options}
          </select>
        </Field>
        <Field label="Al conto">
          <select value={toCode} onChange={(event) => setToCode(event.target.value)}>
            {options}
          </select>
        </Field>
      </div>
      <div className="row">
        <Field label="Importo (€)">
          <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
        </Field>
        <Field label="Entro il">
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>
      </div>
      <Field label="Nota">
        <input value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
      </Field>
    </ConfirmDialog>
  );
}
